import { Link } from 'react-router-dom';

export interface ArticleTemplateData {
  title: string;
  subtitle: string;
  heroImageUrl: string;
  readTime: string;
  sections: {
    heading: string;
    paragraphs: string[];
  }[];
  tips?: string[];
  faqs?: { q: string; a: string }[];
  structuredData?: Record<string, unknown>;
}

export default function ArticleTemplate({ data }: { data: ArticleTemplateData }) {
  return (
    <article>
      <header className="relative w-full min-h-[380px] md:min-h-[450px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={data.heroImageUrl}
            alt={data.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/45"></div>
        </div>
        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-16">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-4">
            <Link to="/" className="hover:text-white/90 transition-colors whitespace-nowrap">Home</Link>
            <span>/</span>
            <span className="text-white/80">Article</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-white leading-tight mb-4">
            {data.title}
          </h1>
          <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto">
            {data.subtitle}
          </p>
          <div className="flex items-center justify-center gap-3 mt-5 text-xs text-white/50">
            <span>Updated: August 2026</span>
            <span>·</span>
            <span>{data.readTime}</span>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="prose-custom">
          {data.sections.map((section, si) => (
            <section key={si} className="mb-10">
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                {section.heading}
              </h2>
              {section.paragraphs.map((p, pi) => (
                <p key={pi} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {p}
                </p>
              ))}
            </section>
          ))}

          {data.tips && data.tips.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                Tips &amp; Best Practices
              </h2>
              <ul className="space-y-3">
                {data.tips.map((tip, i) => (
                  <li key={i} className="flex gap-3 text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-check-line text-primary-500 mt-0.5 shrink-0"></i>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {data.faqs && data.faqs.length > 0 && (
            <section className="mb-10">
              <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-4">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {data.faqs.map((faq, i) => (
                  <details key={i} className="bg-background-100 rounded-lg border border-background-200/70 group">
                    <summary className="px-5 py-3.5 text-sm font-semibold text-foreground-800 cursor-pointer flex items-center justify-between">
                      {faq.q}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <div className="bg-background-100 rounded-lg border border-primary-200 p-6 text-center mt-12">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
              Ready to Start Exploring?
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              Browse organized <strong>Sugargoo Spreadsheet</strong> categories and discover thousands of vetted product finds today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line mr-1.5"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-5 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line mr-1.5"></i>
                Visit Sugargoo Spreadsheet
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}